import { useSelector } from 'react-redux';
import React, { useEffect, useRef } from 'react';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { clsx } from 'clsx';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  useCommonContent,
  useOneCmsLocaleOverviewBody,
  useOneCmsLocaleOverviewDisclaimer,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { RouteParams } from '@/types';
import Flourish from '../icons/Flourish';
import styles from './CenturionLoungeNetworkSection.module.scss';
import LFLink from '../common/LFLink';

const CenturionLoungeNetworkSection = () => {
  const { product } = useRouteParams() as RouteParams;
  const isDeltaReserve = product === 'delta-reserve';
  const cmsImgBaseUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const imagesContainerRef = useRef<HTMLDivElement | null>(null);
  const { partners } = useCommonContent();
  const overviewBody = useOneCmsLocaleOverviewBody();
  const disclaimer = useOneCmsLocaleOverviewDisclaimer();

  useEffect(() => {
    const imagesContainer = imagesContainerRef.current;
    if (!imagesContainer) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            imagesContainer?.classList.add(styles.animate);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 1,
      }
    );

    observer.observe(imagesContainer);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <h3
        className={clsx(
          { 'tw-mb-6': isDeltaReserve },
          'tw-heading-md-regular tw-mt-10 tw-text-gray-900'
        )}
      >
        {partners?.header}
      </h3>
      {!isDeltaReserve && (
        <>
          <p className={!disclaimer ? 'tw-mb-6' : ''}>{overviewBody}</p>
          {disclaimer && <p className="tw-mb-6">{disclaimer}</p>}
        </>
      )}

      <div className="tw-relative tw-hidden tw-h-100 tw-flex-row tw-gap-10 tw-overflow-hidden tw-rounded-2xl tw-border tw-bg-blue-deep-700 tw-px-6 tw-py-8 sm_tw-flex md_tw-h-95 lg_tw-h-113dot75 lg_tw-px-16">
        {/* Images */}
        <div
          className={clsx('tw-flex lg_tw-w-full', styles.imagesContainer)}
          ref={imagesContainerRef}
        >
          <div className={clsx('tw-w-full', styles.imagesLeft)}>
            {/* Image 1 */}
            <div className={clsx('tw-absolute tw-z-10', styles.loungeImage)}>
              {/* large bp */}
              <img
                className="tw-hidden tw-w-full tw-translate-x-45 tw-translate-y-25 tw-transform lg_tw-flex"
                src={`${cmsImgBaseUrl}centurion-network-lounge-large.png`}
                alt="Centurion Lounge"
              />
              {/* medium bp */}
              <img
                className="tw-hidden tw-w-full tw-translate-x-45 tw-translate-y-25 tw-transform md_tw-flex lg_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-lounge-medium.png`}
                alt="Centurion Lounge"
              />
              {/* small bp */}
              <img
                className="tw-flex tw-w-full md_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-lounge-small.png`}
                alt="Centurion Lounge"
              />
            </div>
            {/* image 2 */}
            <div
              className={clsx(
                'tw-relative tw-overflow-hidden tw-rounded-2xl',
                styles.foodImage
              )}
            >
              {/* large bp */}
              <img
                className="tw-hidden tw-w-full lg_tw-flex"
                src={`${cmsImgBaseUrl}centurion-network-food-large.png`}
                alt="Centurion Lounge Food"
              />
              {/* medium bp */}
              <img
                className="tw-flex tw-hidden tw-w-full md_tw-flex lg_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-food-medium.png`}
                alt="Centurion Lounge Food"
              />
              {/* small bp */}
              <img
                className="tw-flex tw-w-full md_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-food-small.png`}
                alt="Centurion Lounge Food"
              />
            </div>
            {/* image 3 medium bp */}
            <div className={clsx('tw-relative lg_tw-hidden', styles.barImage)}>
              <img
                className="tw-hidden tw-w-full md_tw-flex lg_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-bar-medium.png`}
                alt="Centurion Lounge Bar"
              />
              <img
                className="tw-w-full md_tw-hidden"
                src={`${cmsImgBaseUrl}the-centurion-network-bar-small.png`}
                alt="Centurion Lounge Bar"
              />
            </div>
          </div>
          {/* Image 3 large bp */}
          <div
            className={clsx(
              'tw-hidden tw-w-full lg_tw-flex',
              styles.imagesRight
            )}
          >
            <div className={clsx('tw-relative', styles.barImage)}>
              <img
                className="tw-w-full"
                src={`${cmsImgBaseUrl}centurion-network-bar-large.png`}
                alt="Centurion Lounge Bar"
              />
            </div>
          </div>
        </div>

        {/* Megaphone content with button */}
        <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col tw-justify-center">
          <h4 className="tw-heading-sm-bold tw-mb-1 tw-text-white">
            {partners?.cnHeader}
          </h4>
          <p className="tw-mb-6 tw-text-white lg_tw-w-88">
            {partners.cnDescription}
          </p>
          <div>
            <LFLink
              className={`${styles.viewDetailsBtn} btn primary btn-primary flex-justify-center dls-white tw-z-10 tw-max-w-300 tw-px-6`}
              to="centurion-network"
            >
              {partners.cnCTALabel}
            </LFLink>
          </div>
        </div>

        {/* Flourish element absolute */}
        <Flourish
          className="tw-absolute tw-bottom-0 tw-right-6"
          style={{
            maskImage: 'linear-gradient(to left, black 0%, transparent 88%)',
          }}
        />
      </div>
    </>
  );
};

export default CenturionLoungeNetworkSection;
